import React, { useState, useEffect } from 'react';
import _isEmpty from 'lodash/isEmpty';
import './FloatingSelect.scss';
import InnerHTML from '../SetInnerHTML/InnerHTML';
import Heading from '../Heading/Heading';
import '../../Molecules/InputWithFloatingLabel/InputFloatingLabel.scss';

interface FloatingSelectProps {
  value?: string;
  onChange: (value: string, id?: string) => void;
  label?: string;
  optionList: React.JSX.Element[];
  dataTestId: string;
  anyErrorMessage?: string | null;
  className?: string;
  disabled?: boolean;
  handleDropdownVisibilityChange?: (isOpen: boolean | null) => void;
  isDropdownExpanded?: boolean | null;
}

const FloatingSelect: React.FC<FloatingSelectProps> = ({
  optionList,
  value,
  onChange,
  label,
  dataTestId,
  anyErrorMessage,
  className = '',
  disabled = false,
  handleDropdownVisibilityChange,
  isDropdownExpanded = false,
  ...props
}) => {
  const [selectedOption, setSelectedOption] = useState<string | undefined>('');
  const [isLabelFloating, setIsLabelFloating] = useState(false);

  useEffect(() => {
    setSelectedOption(value);
    setIsLabelFloating(!!value);
  }, [value]);

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const valueId = event.target.value;
    setSelectedOption(valueId);
    onChange(valueId);
    setIsLabelFloating(!!selectedOption);
  };

  const handleSelectFocus = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setIsLabelFloating(true);
    onChange(event.target.value);
  };

  const handleSelectBlur = () => {
    setIsLabelFloating(!!selectedOption);
    handleDropdownVisibilityChange?.(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSelectElement>) => {
    if (disabled && (event.key === 'Enter' || event.key === ' ' || event.key !== 'Tab')) {
      event.preventDefault();
    }
  };

  const handleSelectClick = () => {
    handleDropdownVisibilityChange?.(!isDropdownExpanded);
  };

  const errCondition = anyErrorMessage ? 'errorCondition' : '';

  return (
    <div className={`floating-select-container `}>
      <div className={'floating-select-wrapper'}>
        {selectedOption && !_isEmpty(label) && (
          <label htmlFor={label} className={`floating-label ${isLabelFloating ? 'floating' : ''}`}>
            {<InnerHTML Tag="p" text={label!} testAutoId={`${dataTestId}-profile`} />}
          </label>
        )}
        <select
          data-testid={dataTestId}
          id={label}
          className={`floating-select ${errCondition} ${className} ${
            selectedOption ? 'has-value' : ''
          }`}
          value={selectedOption ? selectedOption : ''}
          onChange={handleSelectChange}
          onFocus={handleSelectFocus}
          onBlur={handleSelectBlur}
          onKeyDown={handleKeyDown}
          onClick={handleSelectClick}
          aria-describedby={anyErrorMessage ? 'errorCondition' : ''}
          aria-disabled={disabled}
          {...props}
        >
          {!selectedOption && !_isEmpty(label) && (
            <option value="" disabled hidden>
              {<InnerHTML Tag="p" text={label!} testAutoId={`${dataTestId}-profile-selected`} />}
            </option>
          )}
          {optionList}
        </select>
      </div>
      {anyErrorMessage && (
        <Heading
          level="p"
          className="floating-select__errorMsg"
          id="errorCondition"
          title={anyErrorMessage}
          testAutoId={`${dataTestId}-any-error`}
        />
      )}
    </div>
  );
};

export default FloatingSelect;
